/* SPDX-FileCopyrightText: 2014-present Kriasoft */
/* SPDX-License-Identifier: MIT */

import {
  CardMembership,
  Create,
  Dashboard,
  Image,
  Person,
} from "@mui/icons-material";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  ListItemDecorator,
  Typography,
} from "@mui/joy";
import { ReactNode, memo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useMatch, useNavigate } from "react-router-dom";
import useGoogleLoginStore from "../stores/googleLoginStore";
import useLanguageStore from "../stores/languageStore";
import { getLocalizedPath } from "../utils/urlUtils";
import { BetaBadge } from "./BetaBadge";
import { closeSidebar } from "./sidebar";

export const Navigation = memo(function Navigation(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageStore();
  const userStatus = useGoogleLoginStore(
    (state) =>
      (state as { userStatus: { isAuthenticated: boolean } }).userStatus,
  );
  const setGoogleLoginModalVisible = useGoogleLoginStore(
    (state) => (state as { setVisible: (v: boolean) => void }).setVisible,
  );
  const setSuccessCallback = useGoogleLoginStore(
    (state) =>
      (state as { setSuccessCallback: (cb: () => void) => void })
        .setSuccessCallback,
  );
  const setReturnCallback = useGoogleLoginStore(
    (state) =>
      (state as { setReturnCallback: (cb: () => void) => void })
        .setReturnCallback,
  );

  // Helper function to build language-prefixed paths
  const getLangPath = (path: string): string => {
    return getLocalizedPath(path, currentLanguage);
  };

  return (
    <List
      size="sm"
      sx={{
        gap: 0.5,
        "--List-nestedInsetStart": "30px",
        "--ListItem-radius": (theme) => theme.vars.radius.sm,
      }}
    >
      <NavItem
        path={getLangPath("/discover")}
        label={t("navigation.discover")}
        icon={<Dashboard />}
        navigate={navigate}
      />
      <NavItem
        path={getLangPath("/creator-center")}
        label={t("navigation.myCharacters")}
        icon={<Create />}
        navigate={navigate}
        onClick={() => {
          closeSidebar();
          if (!userStatus?.isAuthenticated) {
            setGoogleLoginModalVisible(true);
            setSuccessCallback(() => {
              navigate(getLangPath("/creator-center"));
            });
            setReturnCallback(() => {});
            return;
          }
          navigate(getLangPath("/creator-center"));
        }}
      />
      <NavItem
        path={getLangPath("/user-persona")}
        label={t("navigation.myPersonas")}
        icon={<Person />}
        navigate={navigate}
        onClick={() => {
          closeSidebar();
          if (!userStatus?.isAuthenticated) {
            setGoogleLoginModalVisible(true);
            setSuccessCallback(() => {
              navigate(getLangPath("/user-persona"));
            });
            setReturnCallback(() => {});
            return;
          }
          navigate(getLangPath("/user-persona"));
        }}
      />
      <NavItem
        path={getLangPath("/create-image")}
        label={
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
            {t("navigation.createImage")}
            <BetaBadge />
          </Box>
        }
        icon={<Image />}
        navigate={navigate}
      />
      {userStatus?.isAuthenticated ? (
        <NavItem
          path={getLangPath("/membership")}
          label={t("navigation.membership")}
          icon={<CardMembership />}
          navigate={navigate}
        />
      ) : null}
    </List>
  );
});

function NavItem(props: NavItemProps): JSX.Element {
  const location = useLocation();

  // Check if current path matches the nav item path
  // Also check path without language prefix for backwards compatibility
  const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}/, "");
  const propsPathWithoutLang = props.path.replace(/^\/[a-z]{2}/, "");
  const isSelected =
    !!useMatch(props.path) ||
    location.pathname === props.path ||
    pathWithoutLang === propsPathWithoutLang;

  return (
    <ListItem>
      <ListItemButton
        component={Link}
        selected={!!isSelected}
        to={props.path}
        aria-current="page"
        onClick={() => {
          props.onClick?.();
          closeSidebar();
        }}
      >
        <ListItemDecorator children={props.icon} />
        <ListItemContent>
          <Typography component="div" sx={{ fontSize: "1.1rem" }}>
            {props.label}
          </Typography>
        </ListItemContent>
      </ListItemButton>
    </ListItem>
  );
}

type NavItemProps = {
  path: string;
  label: ReactNode;
  icon: ReactNode;
  navigate: (path: string) => void;
  onClick?: () => void;
};
